/**
 * PortalImpersonate.js
 * Handles admin impersonation setup in a new browser tab.
 * Reads the impersonation token from the URL, stores it in sessionStorage
 * (keeping admin's localStorage untouched), then redirects to the portal.
 */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spinner, Alert, Container } from 'react-bootstrap';

const PortalImpersonate = () => {
  const { token } = useParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Token di impersonificazione non trovato.');
      return;
    }

    try {
      // Decode the JWT payload to extract user info (no verification needed here,
      // the backend already validated it when generating the token)
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Formato token non valido');
      }

      const payloadJson = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadJson);

      const isImpersonated = payload['IsImpersonated'] === 'true';
      const impersonatedBy = payload['ImpersonatedBy'];

      if (!isImpersonated || !impersonatedBy) {
        throw new Error('Il token non è un token di impersonificazione valido');
      }

      // Build a minimal user object from the JWT claims
      const userId =
        payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
        ] || payload['sub'];
      const email =
        payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'
        ] || payload['email'];
      const firstName =
        payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname'
        ] || payload['given_name'] || '';
      const lastName =
        payload[
          'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname'
        ] || payload['family_name'] || '';
      const rawRoles =
        payload[
          'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
        ] || [];
      const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];

      const userObj = {
        id: userId,
        email,
        loginEmail: email,
        firstName,
        lastName,
        roles,
        isImpersonated: true,
        impersonatedBy,
      };

      // Store in sessionStorage so the admin tab (localStorage) is untouched
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('user', JSON.stringify(userObj));

      // Redirect to the athlete portal with full page reload so App boots cleanly into PortalLayout with sessionStorage
      window.location.replace('/portal/profile');
    } catch (err) {
      console.error('Impersonation setup error:', err);
      setError(
        err.message || 'Errore durante la configurazione della sessione di impersonificazione.'
      );
    }
  }, [token]);

  if (error) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger">
          <Alert.Heading>Sessione non valida</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5 text-center">
      <Spinner animation="border" variant="primary" className="me-2" />
      <span className="text-muted">Avvio sessione impersonificata...</span>
    </Container>
  );
};

export default PortalImpersonate;
